import type { EventType } from './equipment'
import type { TaskClass } from './maintenanceTask'

// ─── Team Schedule (technician view) ─────────────────────────────────────────
//
// The technician-grouped calendar for the execution module: rows are
// technicians, columns are the working days of a week, cells hold assigned
// maintenance events. Mock-backed until the persisted MaintenanceEvent +
// assignee model lands (subtask 86ey7d8n2).

export type Technician = {
  id: string
  name: string
  /** Avatar initial, e.g. "P". */
  initial: string
  /** Short role/shift line, e.g. "Day shift · Calibration". */
  role: string
}

/**
 * Schedule status shown on the cards. `scheduled` = planned, not started.
 *
 * `reserved` belongs only to process-step bookings, which have no maintenance
 * lifecycle — calling one `scheduled` would invite a technician to start work
 * this board does not own.
 */
export type ScheduleStatus = 'scheduled' | 'started' | 'done' | 'overdue' | 'reserved'

export type ScheduleEvent = {
  id: string
  /**
   * The MaintenanceEvent behind this card, when `id` is synthetic.
   *
   * A repeating task is ONE task drawn on many days, so each of its cards needs
   * its own `id` (`<eventId>|<date>`) while every action — start, complete,
   * drag — still has to reach the single task underneath. Absent for an
   * ordinary card, whose `id` already IS the event id.
   */
  taskId?: string
  technicianId: string
  /** 0 = Monday … 4 = Friday of the visible week. */
  dayOffset: number
  /** Equipment unit short label, e.g. "Apheresis-A2". */
  equipmentId: string
  eventType: EventType
  eventClass: TaskClass
  /** Start time, HH:mm 24h. */
  timeStart: string
  planHours: number
  status: ScheduleStatus
  /** "Building-5 / Floor-1 / Bay-5". */
  location: string
  /** Governing SOP, e.g. "SOP-CAL-014". */
  sop: string
  /**
   * True for cards this board only displays. They cannot be dragged, started,
   * ended or reassigned — a process-step booking's lifecycle belongs to the
   * step, not to this schedule.
   */
  readOnly?: boolean
  /** The booked window, when the card represents one (ISO instants). */
  window?: { startsAt: string; endsAt: string }
  /** The procedure a booking was made for, e.g. "MRN-001 · Processing". */
  bookedFor?: string
  /** The actor who made the booking, as recorded on the reservation. */
  bookedBy?: string
}

export const SCHEDULE_STATUS_META: Record<ScheduleStatus, { label: string; color: string }> = {
  scheduled: { label: 'Scheduled', color: '#2563eb' },
  started: { label: 'Started', color: '#d97706' },
  done: { label: 'Done', color: '#16a34a' },
  overdue: { label: 'Overdue', color: '#dc2626' },
  reserved: { label: 'Reserved', color: '#7c3aed' },
}

/** Format a 24h HH:mm as a compact 12h label, e.g. "09:00" → "9:00 AM". */
export function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  const period = h < 12 ? 'AM' : 'PM'
  const hour = h % 12 === 0 ? 12 : h % 12
  return `${hour}:${String(m).padStart(2, '0')} ${period}`
}
